import React from 'react';
import { Button, Card, Input, InputNumber, Select, Table } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { PurchaseOrderOverheadDTO } from '@/services/purchaseOrder.service';

export const PO_OVERHEAD_TYPE_OPTIONS = [
  'Freight',
  'Insurance',
  'Customs Duty',
  'Handling',
  'Clearing',
  'Packaging',
  'Administrative Overhead',
  'Project Overhead',
  'Other',
] as const;

export type PoOverheadDraft = Partial<PurchaseOrderOverheadDTO> & { id?: string };

type Props = {
  overheads: PoOverheadDraft[];
  onChange: (next: PoOverheadDraft[]) => void;
  currency?: string;
  readOnly?: boolean;
  formatAmount?: (n: number) => string;
};

const PoOverheadEditor: React.FC<Props> = ({
  overheads,
  onChange,
  currency = 'AED',
  readOnly = false,
  formatAmount = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }),
}) => {
  const updateRow = (idx: number, patch: Partial<PoOverheadDraft>) => {
    onChange(overheads.map((oh, i) => (i === idx ? { ...oh, ...patch } : oh)));
  };

  return (
    <Card
      size="small"
      title="Overheads (landed cost)"
      style={{ marginTop: 16 }}
      extra={
        readOnly ? null : (
          <Button
            icon={<PlusOutlined />}
            onClick={() =>
              onChange([
                ...overheads,
                {
                  id: `draft-oh-${Date.now()}`,
                  line_number: overheads.length + 1,
                  overhead_type: 'Freight',
                  currency,
                  exchange_rate: 1,
                  original_amount: 0,
                  amount: 0,
                },
              ])
            }
          >
            Add overhead
          </Button>
        )
      }
    >
      <Table
        size="small"
        pagination={false}
        rowKey={(r, idx) => String(r.id || idx)}
        dataSource={overheads}
        locale={{ emptyText: 'No overheads — supplier unit price is used at GRN' }}
        columns={[
          {
            title: '#',
            width: 50,
            render: (_: unknown, __: unknown, idx: number) => idx + 1,
          },
          {
            title: 'Type',
            dataIndex: 'overhead_type',
            width: 160,
            render: (v: string, _r: PoOverheadDraft, idx: number) =>
              readOnly ? (
                v
              ) : (
                <Select
                  style={{ width: '100%' }}
                  value={v}
                  options={PO_OVERHEAD_TYPE_OPTIONS.map((t) => ({ value: t, label: t }))}
                  onChange={(val) => updateRow(idx, { overhead_type: val })}
                />
              ),
          },
          {
            title: 'Description',
            dataIndex: 'description',
            render: (v: string, _r: PoOverheadDraft, idx: number) =>
              readOnly ? (
                v || '—'
              ) : (
                <Input value={v} onChange={(e) => updateRow(idx, { description: e.target.value })} />
              ),
          },
          {
            title: 'Amount',
            dataIndex: 'original_amount',
            align: 'right' as const,
            width: 140,
            render: (v: number, _r: PoOverheadDraft, idx: number) =>
              readOnly ? (
                formatAmount(Number(v || 0))
              ) : (
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  value={v}
                  onChange={(val) =>
                    updateRow(idx, {
                      original_amount: Number(val || 0),
                      amount: Number(val || 0),
                    })
                  }
                />
              ),
          },
          {
            title: 'Remarks',
            dataIndex: 'remarks',
            render: (v: string, _r: PoOverheadDraft, idx: number) =>
              readOnly ? (
                v || '—'
              ) : (
                <Input value={v} onChange={(e) => updateRow(idx, { remarks: e.target.value })} />
              ),
          },
          ...(readOnly
            ? []
            : [
                {
                  title: '',
                  width: 48,
                  render: (_: unknown, __: PoOverheadDraft, idx: number) => (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => onChange(overheads.filter((_, i) => i !== idx))}
                    />
                  ),
                },
              ]),
        ]}
      />
      {overheads.length > 0 ? (
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          Total overhead:{' '}
          <strong>
            {formatAmount(
              overheads.reduce((s, o) => s + Number(o.amount ?? o.original_amount ?? 0), 0)
            )}
          </strong>
        </div>
      ) : null}
    </Card>
  );
};

export default PoOverheadEditor;
